/**
 * Dier bewerken en dier toevoegen (sessie 73 blok 2, ontwerp 31 punt 4).
 *
 * Laptop: een vlak binnen de kaart. iPhone: het vlak schuift omhoog over
 * de kaart. Altijd een kruisje rechtsboven en de hoofdknop onderaan op
 * dezelfde plek. Opslaan stuurt alleen wat veranderd is; een leeg gemaakt
 * veld gaat mee in `wissen`. Daarna ververst de klantkaart.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useMemo, useState, type ReactNode } from "react";

import { vertaalApiFout } from "@/lib/api";
import type { Diersoort } from "@/types/api";

import { v6Api } from "../../api";
import { Icoon } from "../../Icoon";
import { v6Sleutels } from "../../sleutels";
import { datumKort, geslachtWoord } from "../opmaak";
import type { KaartDier, KaartFotoKeuze, RasKeuze } from "../types";
import {
  ALLERGENEN,
  bouwAllergieen,
  bouwWijziging,
  conditieWoord,
  invoerUit,
  leesGewicht,
  type DierInvoer,
  type VermijdStand,
} from "./dierInvoer";
import { useAfbeelding, useSmal } from "./smal";

// ── Vaste lijsten (alleen scherm) ───────────────────────────────────

const STAND_WOORD: Record<VermijdStand, string> = {
  wens: "wens",
  intolerantie: "intolerantie volgens klant",
  allergie: "allergie",
  allergie_dierenarts: "allergie vastgesteld door dierenarts",
};

/** 0 tot 12 uur in stapjes van een half uur. */
const BEWEGING = Array.from({ length: 25 }, (_, i) => i / 2);

function bewegingWoord(uren: number): string {
  if (uren === 0.5) return "een half uur";
  return `${String(uren).replace(".", ",")} uur`;
}

const DIERSOORTEN: Diersoort[] = ["hond", "kat", "knaagdier", "vogel", "vis", "reptiel", "anders"];

// ── Vlak (kop met kruisje, inhoud, hoofdknop onderaan) ───────────────

export function Vlak({
  titel,
  onSluit,
  knop,
  children,
}: {
  titel: string;
  onSluit: () => void;
  knop: ReactNode;
  children: ReactNode;
}) {
  const smal = useSmal();
  return (
    <>
      {smal ? <div className="kk-vlak-overlay" data-testid="kk-vlak-overlay" onClick={onSluit} /> : null}
      <div
        className={`kk-vlak${smal ? " schuift" : ""}`}
        role={smal ? "dialog" : "group"}
        aria-modal={smal || undefined}
        aria-label={titel}
      >
        <div className="kk-vlak-kop">
          <b>{titel}</b>
          <button type="button" className="kk-kruis" aria-label="Sluiten" title="Sluiten" onClick={onSluit}>
            <Icoon naam="x" klein />
          </button>
        </div>
        <div className="kk-vlak-inhoud">{children}</div>
        <div className="kk-vlak-voet">{knop}</div>
      </div>
    </>
  );
}

function Keuze<T extends string | number | boolean | null>({
  label,
  waarde,
  opties,
  onKies,
}: {
  label: string;
  waarde: T;
  opties: { waarde: T; woord: string }[];
  onKies: (w: T) => void;
}) {
  return (
    <div className="kk-veld">
      <span className="l">{label}</span>
      <div className="kk-segment" role="group" aria-label={label}>
        {opties.map((o) => (
          <button
            key={String(o.waarde)}
            type="button"
            aria-pressed={waarde === o.waarde}
            onClick={() => onKies(o.waarde)}
          >
            {o.woord}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Ras met keuzelijst ───────────────────────────────────────────────

function RasVeld({
  diersoort,
  ras,
  onZet,
}: {
  diersoort: string;
  ras: string;
  onZet: (ras: string, sleutel: string | null) => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const rassen = useQuery({
    queryKey: ["v6", "rassen", diersoort],
    queryFn: () => v6Api.rassen(diersoort),
    staleTime: Infinity,
  });
  const lijst = rassen.data ?? [];
  const zoek = ras.trim().toLowerCase();
  const passend = (zoek ? lijst.filter((r) => r.naam.toLowerCase().includes(zoek)) : lijst).slice(0, 8);
  const exact = (tekst: string): RasKeuze | undefined =>
    lijst.find((r) => r.naam.toLowerCase() === tekst.trim().toLowerCase());
  return (
    <div className="kk-veld kk-ras">
      <label className="l" htmlFor={id}>
        Ras
      </label>
      <input
        id={id}
        type="text"
        autoComplete="off"
        value={ras}
        role="combobox"
        aria-expanded={open && passend.length > 0}
        aria-controls={`${id}-lijst`}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          const tekst = e.target.value;
          onZet(tekst, exact(tekst)?.sleutel ?? null);
          setOpen(true);
        }}
      />
      {open && passend.length > 0 ? (
        <ul className="kk-raslijst" id={`${id}-lijst`} role="listbox" aria-label="Rassen">
          {passend.map((r) => (
            <li key={r.sleutel} role="option" aria-selected={r.naam === ras}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onZet(r.naam, r.sleutel);
                  setOpen(false);
                }}
              >
                {r.naam}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ── Foto kiezen ──────────────────────────────────────────────────────

function Duim({ foto, onKies, bezig }: { foto: KaartFotoKeuze; onKies: () => void; bezig: boolean }) {
  const { src } = useAfbeelding(foto.url);
  return (
    <button
      type="button"
      className="kk-duim"
      disabled={bezig}
      aria-label={`Foto van ${datumKort(foto.moment)}, ${foto.kanaal}`}
      onClick={onKies}
    >
      {src ? <img src={src} alt="" /> : <span className="leeg" />}
    </button>
  );
}

function FotoKeuze({ klantId, dier }: { klantId: string; dier: KaartDier }) {
  const queryClient = useQueryClient();
  const [kiezen, setKiezen] = useState(false);
  const fotos = useQuery({
    queryKey: ["v6", "klantfotos", klantId],
    queryFn: () => v6Api.klantFotos(klantId),
    enabled: kiezen,
  });
  const ververs = () => queryClient.invalidateQueries({ queryKey: v6Sleutels.klantKaart(klantId) });
  const zet = useMutation({
    mutationFn: (berichtId: string) => v6Api.dierFotoZet(klantId, dier.id, berichtId),
    onSuccess: () => {
      setKiezen(false);
      return ververs();
    },
  });
  const weg = useMutation({
    mutationFn: () => v6Api.dierFotoWeg(klantId, dier.id),
    onSuccess: ververs,
  });
  return (
    <div className="kk-veld kk-fotokeuze">
      <span className="l">Foto</span>
      <div className="kk-rij">
        <button type="button" className="kk-tekstknop" onClick={() => setKiezen((k) => !k)}>
          Foto kiezen
        </button>
        {dier.foto_url ? (
          <button
            type="button"
            className="kk-tekstknop"
            disabled={weg.isPending}
            onClick={() => weg.mutate()}
          >
            Foto weg
          </button>
        ) : null}
      </div>
      {kiezen ? (
        fotos.isLoading ? (
          <p className="stil">Foto&apos;s worden geladen.</p>
        ) : fotos.isError ? (
          <p className="v6-fout" role="alert">
            De foto&apos;s konden niet geladen worden.
          </p>
        ) : (fotos.data ?? []).length === 0 ? (
          <p className="stil">Geen foto&apos;s in de gesprekken.</p>
        ) : (
          <div className="kk-duimen" data-testid="foto-raster">
            {(fotos.data ?? []).map((f) => (
              <Duim key={f.bericht_id} foto={f} bezig={zet.isPending} onKies={() => zet.mutate(f.bericht_id)} />
            ))}
          </div>
        )
      ) : null}
      {zet.isError || weg.isError ? (
        <p className="v6-fout" role="alert">
          De foto kon niet gewijzigd worden.
        </p>
      ) : null}
    </div>
  );
}

// ── Bewerkvlak ───────────────────────────────────────────────────────

export function Bewerkvlak({
  klantId,
  dier,
  onSluit,
}: {
  klantId: string;
  dier: KaartDier;
  onSluit: () => void;
}) {
  const queryClient = useQueryClient();
  const ids = useId();
  const [invoer, setInvoer] = useState<DierInvoer>(() => invoerUit(dier));
  const zet = <K extends keyof DierInvoer>(k: K, w: DierInvoer[K]) =>
    setInvoer((oud) => ({ ...oud, [k]: w }));

  const gewichtFout = leesGewicht(invoer.gewicht) === undefined;
  const allergenen = useMemo(() => {
    const extra = dier.vermijden.map((v) => v.allergeen).filter((a) => !(ALLERGENEN as readonly string[]).includes(a));
    return [...ALLERGENEN, ...extra];
  }, [dier.vermijden]);
  const hadAllergie = useMemo(
    () => new Set(dier.vermijden.filter((v) => v.stand === "allergie" && !v.vastgesteld_dierenarts).map((v) => v.allergeen)),
    [dier.vermijden],
  );

  const opslaan = useMutation({
    mutationFn: async () => {
      const body = bouwWijziging(dier, invoer);
      const rijen = bouwAllergieen(dier, invoer);
      if (body) await v6Api.huisdierWijzig(dier.id, body);
      if (rijen) await v6Api.huisdierAllergieen(dier.id, rijen);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: v6Sleutels.klantKaart(klantId) });
      onSluit();
    },
  });

  const wisselAllergeen = (a: string) =>
    setInvoer((oud) => {
      const vermijden = { ...oud.vermijden };
      if (vermijden[a]) delete vermijden[a];
      else vermijden[a] = "wens";
      return { ...oud, vermijden };
    });

  const man = geslachtWoord(dier.diersoort, "mannelijk") ?? "mannelijk";
  const vrouw = geslachtWoord(dier.diersoort, "vrouwelijk") ?? "vrouwelijk";

  return (
    <Vlak
      titel={`${dier.naam} bewerken`}
      onSluit={onSluit}
      knop={
        <button
          type="button"
          className="v6-knop doe"
          disabled={opslaan.isPending || !invoer.naam.trim() || gewichtFout}
          onClick={() => opslaan.mutate()}
        >
          Opslaan
        </button>
      }
    >
      <FotoKeuze klantId={klantId} dier={dier} />

      <div className="kk-veld">
        <label className="l" htmlFor={`${ids}-naam`}>
          Naam
        </label>
        <input
          id={`${ids}-naam`}
          type="text"
          value={invoer.naam}
          onChange={(e) => zet("naam", e.target.value)}
        />
      </div>

      <RasVeld
        diersoort={dier.diersoort}
        ras={invoer.ras}
        onZet={(ras, sleutel) => setInvoer((oud) => ({ ...oud, ras, rasSleutel: sleutel }))}
      />

      <Keuze
        label="Geslacht"
        waarde={invoer.geslacht}
        opties={[
          { waarde: "mannelijk", woord: man },
          { waarde: "vrouwelijk", woord: vrouw },
          { waarde: null, woord: "onbekend" },
        ]}
        onKies={(w) => zet("geslacht", w)}
      />

      <Keuze
        label="Castratie"
        waarde={invoer.castratie}
        opties={[
          { waarde: true, woord: "ja" },
          { waarde: false, woord: "nee" },
          { waarde: null, woord: "onbekend" },
        ]}
        onKies={(w) => zet("castratie", w)}
      />

      <div className="kk-veld">
        <label className="l" htmlFor={`${ids}-geboren`}>
          Geboortedatum
        </label>
        <div className="kk-rij">
          <input
            id={`${ids}-geboren`}
            type="date"
            value={invoer.geboortedatum}
            onChange={(e) => zet("geboortedatum", e.target.value)}
          />
          <div className="kk-segment" role="group" aria-label="Geboortedatum exact of geschat">
            <button type="button" aria-pressed={!invoer.geschat} onClick={() => zet("geschat", false)}>
              exact
            </button>
            <button type="button" aria-pressed={invoer.geschat} onClick={() => zet("geschat", true)}>
              geschat
            </button>
          </div>
        </div>
      </div>

      <div className="kk-veld">
        <label className="l" htmlFor={`${ids}-gewicht`}>
          Gewicht (kg)
        </label>
        <input
          id={`${ids}-gewicht`}
          type="text"
          inputMode="decimal"
          className="kort"
          value={invoer.gewicht}
          aria-invalid={gewichtFout || undefined}
          onChange={(e) => zet("gewicht", e.target.value)}
        />
        {gewichtFout ? <small className="kk-veldfout">geen geldig gewicht</small> : null}
      </div>

      <div className="kk-veld">
        <span className="l">Conditie</span>
        <div className="kk-conditie" role="group" aria-label="Conditie, 1 tot 9">
          {Array.from({ length: 9 }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={invoer.bcs === n}
              aria-label={`${n} van 9, ${conditieWoord(n)}`}
              data-groep={conditieWoord(n)}
              onClick={() => zet("bcs", invoer.bcs === n ? null : n)}
            >
              <b className="num">{n}</b>
              <small>{conditieWoord(n)}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="kk-veld">
        <label className="l" htmlFor={`${ids}-beweging`}>
          Beweging per dag
        </label>
        <select
          id={`${ids}-beweging`}
          value={invoer.beweging === null ? "" : String(invoer.beweging)}
          onChange={(e) => zet("beweging", e.target.value === "" ? null : Number(e.target.value))}
        >
          <option value="">onbekend</option>
          {BEWEGING.map((u) => (
            <option key={u} value={String(u)}>
              {bewegingWoord(u)}
            </option>
          ))}
        </select>
      </div>

      <div className="kk-veld">
        <span className="l">Vermijden</span>
        <div className="kk-allergenen" role="group" aria-label="Vermijden">
          {allergenen.map((a) => (
            <button
              key={a}
              type="button"
              aria-pressed={Boolean(invoer.vermijden[a])}
              onClick={() => wisselAllergeen(a)}
            >
              {a}
            </button>
          ))}
        </div>
        {Object.keys(invoer.vermijden).length > 0 ? (
          <ul className="kk-vermijdrijen">
            {allergenen
              .filter((a) => invoer.vermijden[a])
              .map((a) => (
                <li key={a}>
                  <b>{a}</b>
                  <select
                    aria-label={`Stand ${a}`}
                    value={invoer.vermijden[a]}
                    onChange={(e) =>
                      setInvoer((oud) => ({
                        ...oud,
                        vermijden: { ...oud.vermijden, [a]: e.target.value as VermijdStand },
                      }))
                    }
                  >
                    <option value="wens">{STAND_WOORD.wens}</option>
                    <option value="intolerantie">{STAND_WOORD.intolerantie}</option>
                    {hadAllergie.has(a) ? <option value="allergie">{STAND_WOORD.allergie}</option> : null}
                    <option value="allergie_dierenarts">{STAND_WOORD.allergie_dierenarts}</option>
                  </select>
                </li>
              ))}
          </ul>
        ) : null}
      </div>

      {opslaan.isError ? (
        <p className="v6-fout" role="alert">
          Opslaan lukte niet: {vertaalApiFout(opslaan.error)}
        </p>
      ) : null}
    </Vlak>
  );
}

// ── Nieuw dier ───────────────────────────────────────────────────────

export function NieuwDier({
  klantId,
  onSluit,
  onGemaakt,
}: {
  klantId: string;
  onSluit: () => void;
  onGemaakt: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const ids = useId();
  const [naam, setNaam] = useState("");
  const [soort, setSoort] = useState<Diersoort>("hond");
  const maak = useMutation({
    mutationFn: () => v6Api.nieuwDier(klantId, { naam: naam.trim(), diersoort: soort }),
    onSuccess: async (dier) => {
      await queryClient.invalidateQueries({ queryKey: v6Sleutels.klantKaart(klantId) });
      onGemaakt(dier.id);
    },
  });
  return (
    <Vlak
      titel="Nieuw dier"
      onSluit={onSluit}
      knop={
        <button
          type="button"
          className="v6-knop doe"
          disabled={!naam.trim() || maak.isPending}
          onClick={() => maak.mutate()}
        >
          Toevoegen
        </button>
      }
    >
      <div className="kk-veld">
        <label className="l" htmlFor={`${ids}-naam`}>
          Naam
        </label>
        <input id={`${ids}-naam`} type="text" value={naam} onChange={(e) => setNaam(e.target.value)} />
      </div>
      <div className="kk-veld">
        <label className="l" htmlFor={`${ids}-soort`}>
          Soort
        </label>
        <select id={`${ids}-soort`} value={soort} onChange={(e) => setSoort(e.target.value as Diersoort)}>
          {DIERSOORTEN.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {maak.isError ? (
        <p className="v6-fout" role="alert">
          Toevoegen lukte niet: {vertaalApiFout(maak.error)}
        </p>
      ) : null}
    </Vlak>
  );
}
